import { makeStyles } from '@mui/styles';
import Banner from 'apollo-react/components/Banner';
import Grid from 'apollo-react/components/Grid';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Calendar from './components/Calendar';
import { getSavedSearchResults } from '../../actions/SavedSearchResultsActions';
import Layout from '../../components/Layout';
import AdvancedSearchModal from '../../components/SavedSearchesTable/AdvancedSearchModal';
import * as savedSearchesSelectors from '../../components/SavedSearchesTable/selectors';
import { mapAdvancedSearchCriteria } from '../../components/SavedSearchesTable/utils';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getTranslations } from '../../form-designer/selectors/common.selectors';
import { parseNavigationColumnParams } from '../../utils/legacyRouteMapper';

const useStyles = makeStyles({
  container: {
    height: 'calc(100vh - 64px)',
    overflowY: 'auto',
    padding: 16,
  },
});

function CalendarPage() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const translations = useSelector(getTranslations);

  const [error, setError] = useState();
  const [events, setEvents] = useState([]);
  const [refresh, setRefresh] = useState();
  const [showFilter, setShowFilter] = useState(false);
  const [advancedSearchCriteria, setAdvancedSearchCriteria] = useState();

  const entityName = 'PlannedAuditSchedule';
  const searchName = 'Planned Audits Schedule';

  const savedSearchResults = useSelector((state) =>
    savedSearchesSelectors.getResultsState(state, { entityName, searchName })
  );

  const mappedAdvancedSearchCriteria = useMemo(
    () => mapAdvancedSearchCriteria(advancedSearchCriteria),
    [advancedSearchCriteria]
  );

  useEffect(() => {
    document.title = 'Calendar';

    return () => {
      sessionStorage.removeItem('backButtonURL');
    };
  }, []);

  useEffect(() => {
    document.title = translateString('Calendar');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations.system]);

  useEffect(() => {
    dispatch(
      getSavedSearchResults({
        entityName,
        searchName,
        advancedsearchcriteria: mappedAdvancedSearchCriteria,
        pageSize: 100,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedSearchCriteria, refresh]);

  useEffect(() => {
    const rows = savedSearchResults?.results?.rows ?? [];

    setEvents(
      rows
        .filter((row) => row.PLANNED_START_DATE && row.PLANNED_END_DATE)
        .map((row) => {
          const {
            AUDIT_TITLE: title,
            PLANNED_START_DATE: startDate,
            PLANNED_END_DATE: endDate,
            AUDITEE_CODE: desc,
            AUD_PROG_RECORD_NUMBER: selector,
            NavigationColumn: navigationColumn,
            NavigationColumnParams: navigationColumnParams,
          } = row;

          const event = {
            allDay: true,
            title: navigationColumn ? `${row[navigationColumn]} - ${title}` : title,
            start: moment(startDate)
              .startOf('day')
              .toDate(),
            end: moment(endDate)
              .endOf('day')
              .toDate(),
            link: parseNavigationColumnParams(navigationColumnParams).url,
            desc,
            selector,
            data: row,
          };

          return event;
        })
    );
  }, [savedSearchResults]);

  function handleSelectEvent(e) {
    history.push(e.link);
  }

  function handleShowFilter() {
    setShowFilter(true);
  }

  function handleRefresh() {
    setRefresh(Date.now());
  }

  function handleCloseFilter() {
    setShowFilter(false);
  }

  function handleSearch(criteria) {
    setAdvancedSearchCriteria(criteria.filter((c) => !c.isCustom));
  }

  function renderEventTitle(event) {
    const navigationColumn = event.data.NavigationColumn;

    if (navigationColumn) {
      return (
        <>
          <span ui-ta={navigationColumn}>{event.data[navigationColumn]}</span>
          {' - '}
          <span ui-ta="AUDIT_TITLE">{event.data.AUDIT_TITLE}</span>
        </>
      );
    }

    return <span ui-ta="AUDIT_TITLE">{event.data.AUDIT_TITLE}</span>;
  }

  return (
    <Layout>
      <Banner variant="warning" open={!!error} message={error} onClick={() => setError()} />
      <Grid container className={classes.container} ui-ta="ScrollingElement">
        <Grid item md={12}>
          <Calendar
            events={events}
            onSelectEvent={handleSelectEvent}
            onShowFilter={handleShowFilter}
            onRefresh={handleRefresh}
            renderEventTitle={renderEventTitle}
          />

          <AdvancedSearchModal
            open={showFilter}
            onClose={handleCloseFilter}
            entityName={entityName}
            searchName={searchName}
            onSearch={handleSearch}
            initialCriteria={advancedSearchCriteria}
          />
        </Grid>
      </Grid>
    </Layout>
  );
}

export default CalendarPage;
